import {
  DEFAULT_LAT,
  DEFAULT_RADIUS_IN_METERS,
  DEFAULT_LON,
  DEFAULT_ADDRESS,
  CLUSTER_RADIUS_IN_METERS
} from '@/modules/accredited-establishments/constants'
import { getRadiusInMeters } from '@/modules/accredited-establishments/functions'
import {
  AddressPayload,
  CoordinatesPayload,
  FiltersState,
  SearchLocation,
  UpdateSearchLocationPayload,
  InitFiltersPayload,
  UpdateClusterLocationPayload,
  UpdateFiltersPayload
} from './useFiltersReducer.types'

const isCoordinatesPayload = (
  payload: UpdateSearchLocationPayload
): payload is CoordinatesPayload => {
  return 'lat' in payload && 'lon' in payload
}

const toSearchLocation = (
  payload: UpdateSearchLocationPayload
): SearchLocation => {
  if (isCoordinatesPayload(payload)) {
    return {
      type: 'coordinates',
      lat: payload.lat,
      lon: payload.lon,
      radiusInMeters: payload.bounds
        ? getRadiusInMeters(payload.bounds)
        : undefined
    }
  }

  return {
    type: 'address',
    address: payload.address
  }
}

/**
 * Action Handlers
 */

const initFilters = (
  state: FiltersState,
  payload: InitFiltersPayload
): FiltersState => {
  const { address } = payload

  if (address) {
    const location = toSearchLocation({ address })

    return {
      ...state,
      ...payload,
      ready: true,
      address,
      markerLocation: location,
      establishmentListLocation: location
    }
  }

  /**
   * If no filters are provided, use default address location for establishment list
   * and default coordinates(lat, lon and radius) location for cluster and marker
   */

  const defaultLocation = {
    lat: DEFAULT_LAT,
    lon: DEFAULT_LON
  }

  const defaultAddress = toSearchLocation({ address: DEFAULT_ADDRESS })
  const defaultCoordinates = {
    ...toSearchLocation(defaultLocation),
    radiusInMeters: DEFAULT_RADIUS_IN_METERS
  }

  return {
    ...state,
    ...payload,
    ready: true,
    address: DEFAULT_ADDRESS,
    markerLocation: defaultCoordinates,
    establishmentListLocation: defaultAddress
  }
}

const setProviderName = (
  state: FiltersState,
  payload: string
): FiltersState => {
  return { ...state, providerName: payload }
}

const updateFilters = (
  state: FiltersState,
  payload: UpdateFiltersPayload
): FiltersState => ({
  ...state,
  ...payload
})

const updateMarkerLocation = (
  state: FiltersState,
  payload: UpdateSearchLocationPayload
): FiltersState => {
  return {
    ...state,
    markerLocation: toSearchLocation(payload)
  }
}

const updateClusterLocation = (
  state: FiltersState,
  payload: UpdateClusterLocationPayload
): FiltersState => {
  return {
    ...state,
    clusterLocation: {
      lat: payload.lat,
      lon: payload.lon,
      radiusInMeters: CLUSTER_RADIUS_IN_METERS
    }
  }
}

const updateEstablishmentListLocation = (
  state: FiltersState,
  payload: UpdateSearchLocationPayload
): FiltersState => {
  return {
    ...state,
    establishmentListLocation: toSearchLocation(payload)
  }
}

const updateAddress = (
  state: FiltersState,
  payload: AddressPayload
): FiltersState => {
  return {
    ...state,
    address: payload.address,
    markerLocation: toSearchLocation(payload),
    establishmentListLocation: toSearchLocation(payload)
  }
}

const updateCoordinates = (
  state: FiltersState,
  payload: CoordinatesPayload
): FiltersState => {
  return {
    ...state,
    markerLocation: toSearchLocation(payload),
    establishmentListLocation: toSearchLocation(payload)
  }
}

export {
  initFilters,
  setProviderName,
  updateFilters,
  updateMarkerLocation,
  updateClusterLocation,
  updateEstablishmentListLocation,
  updateCoordinates,
  updateAddress
}
